import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { qk } from './keys'
import type { AdminWebsiteContent, WebsiteHistory, WebsiteKind, WebsitePayload } from '../types/api'
export function useWebsiteContent() {
  return useQuery({
    queryKey: qk.website,
    queryFn: () => api.get<AdminWebsiteContent[]>('/admin/website/content'),
  })
}
export function useWebsiteHistory(id: string) {
  return useQuery({
    queryKey: qk.websiteHistory(id),
    queryFn: () => api.get<WebsiteHistory[]>(`/admin/website/content/${id}/history`),
    enabled: Boolean(id),
  })
}
export function useWebsiteCatalog() {
  return useQuery({
    queryKey: qk.websiteCatalog,
    queryFn: async () => {
      const [subjects, tutors, plans] = await Promise.all([
        api.get<{ id: string; nameEn: string; nameAr?: string }[]>('/catalog/subjects'),
        api.get<{ data: { id: string; fullName: string }[] }>('/tutors', {
          limit: 100,
          verifiedOnly: true,
        }),
        api.get<{ id: string; nameEn: string; nameAr?: string }[]>('/bundles'),
      ])
      return { subjects, tutors: tutors.data, plans }
    },
  })
}
export function useWebsiteMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      action: 'create' | 'save' | 'publish' | 'unpublish'
      id?: string
      revision?: number
      kind: WebsiteKind
      slug: string
      content: WebsitePayload
    }) => {
      if (input.action === 'create')
        return api.post<AdminWebsiteContent>('/admin/website/content', {
          kind: input.kind,
          slug: input.slug,
          content: input.content,
        })
      if (input.action === 'save')
        return api.put<AdminWebsiteContent>(`/admin/website/content/${input.id}`, {
          expectedRevision: input.revision,
          content: input.content,
        })
      return api.post<AdminWebsiteContent>(`/admin/website/content/${input.id}/${input.action}`, {
        expectedRevision: input.revision,
      })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.website }),
  })
}
